import { useState } from "react";
import { NavLink, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard, FilePlus, FileText, Users,
  LogOut, ChevronLeft, ChevronRight, CreditCard, Shield, Lock, Sparkles,
} from "lucide-react";
import { cn } from "../lib/utils.js";
import { Button } from "./ui/button.js";
import { ThemeToggle } from "./theme-toggle.js";
import { Separator } from "./ui/separator.js";

interface SidebarProps {
  onLogout: () => void;
  userEmail: string | null;
  userRole?: string | null;
}

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/emitir", label: "Emitir", icon: FilePlus },
  { to: "/emitidos", label: "Emitidos", icon: FileText },
  { to: "/clientes", label: "Clientes", icon: Users },
  { to: "/suscripcion", label: "Suscripción", icon: CreditCard },
  { to: "/seguridad", label: "Seguridad", icon: Lock },
  { to: "/mockup-acciones", label: "Mockup UI", icon: Sparkles },
];

const adminNavItem = { to: "/admin", label: "Admin", icon: Shield };

export function Sidebar({ onLogout, userEmail, userRole }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const pageTitle = navItems.find((item) => location.pathname.startsWith(item.to))?.label ?? "FolioListo";

  return (
    <div className="flex h-screen w-full">
      <aside
        className={cn(
          "flex flex-col border-r bg-card transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex h-20 items-center gap-2 border-b px-4">
          <img src="/logo.svg" alt="FolioListo" className="h-16 w-16 shrink-0" />
          {!collapsed && (
            <span className="font-heading font-bold text-xl">
              Folio<span className="text-primary">Listo</span>
            </span>
          )}
        </div>

        <nav className="flex-1 space-y-1 p-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
          {userRole === "ADMIN" && (
            <NavLink
              to={adminNavItem.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
              title={collapsed ? adminNavItem.label : undefined}
            >
              <adminNavItem.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{adminNavItem.label}</span>}
            </NavLink>
          )}
        </nav>

        <Separator />

        <div className="p-2">
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {!collapsed && (
              <span className="text-xs text-muted-foreground truncate flex-1">
                {userEmail ?? "usuario"}
              </span>
            )}
          </div>
        </div>

        <div className="p-2">
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full justify-start gap-3"
            title={collapsed ? "Cerrar sesión" : undefined}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!collapsed && <span>Cerrar sesión</span>}
          </Button>
        </div>

        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full"
            title={collapsed ? "Expandir" : "Colapsar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 items-center border-b px-6">
          <h1 className="text-lg font-semibold">{pageTitle}</h1>
        </header>
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

